import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Banknote, ClipboardList, FilePlus2, FileText, GitPullRequestArrow, Lock, PiggyBank, Receipt, Save, TrendingUp, Truck, LockOpen, Wallet } from 'lucide-react'
import { getJob, releaseRetainage, requestDeposit, saveJob } from '../../api/jobs'
import { getCustomers } from '../../api/master'
import ErrorBanner from '../../components/ErrorBanner'
import Button from '../../components/Button'
import Modal from '../../components/Modal'
import HelpTip from '../../components/HelpTip'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import { Card, Tile } from '../../components/Summary'
import { useToast } from '../../components/Toast.jsx'
import { useSession } from '../../session'
import { fmtDay } from '../../utils/format'
import { Field, customerName } from '../master/masterShared'
import { JOB_STATUS, marginText } from './JobList'
import JobChanges from './JobChanges'
import JobPhotos from './JobPhotos'
import { JobMeasurements } from './Measurements'
import { JobProfitGuide, JobsGuide } from './JobsGuide'
import { money } from './salesShared'
import { CreateOption } from '../../components/CreateOption'

const signedMoney = (n) => `${n < 0 ? '−' : '+'}${money(Math.abs(n))}`

// Bill statuses as people say them.
const BILL_STATUS = { PostedToAP: 'Posted', ReturnedToPurchasing: 'Returned to purchasing' }

const BLANK = { name: '', customer_id: '', site_address: '', status: 'Open', start_date: '', end_date: '', notes: '', track_progress: false, estimated_cost: '', contract_price: '', retainage_percent: '', measurement_id: '' }

// One job: what it earned and cost, every document on it, and its details.
export default function JobDetail() {
  const { jobId } = useParams()
  const isNew = !jobId
  const nav = useNavigate()
  const { notify } = useToast()
  const { has, measurements } = useSession()
  const [job, setJob] = useState(null)
  const [form, setForm] = useState(BLANK)
  const [customers, setCustomers] = useState([])
  const [errors, setErrors] = useState({})
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [depositOpen, setDepositOpen] = useState(false)
  const [depositAmount, setDepositAmount] = useState('')
  const [depositing, setDepositing] = useState(false)
  const [releaseOpen, setReleaseOpen] = useState(false)
  const [releaseAmount, setReleaseAmount] = useState('')
  const [releasing, setReleasing] = useState(false)

  const load = () => getJob(jobId).then((j) => {
    setJob(j)
    setForm({ ...Object.fromEntries(Object.keys(BLANK).map((k) => [k, j[k] == null ? '' : String(j[k])])), track_progress: !!j.track_progress })
  })

  useEffect(() => {
    getCustomers().then((rows) => setCustomers(Array.isArray(rows) ? rows : [])).catch(() => {})
    if (isNew) { setJob(null); setForm(BLANK); return }
    setLoading(true)
    load().catch((e) => setError(e.message || 'Couldn’t load the job.')).finally(() => setLoading(false))
  }, [jobId]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch) => setForm((f) => ({ ...f, ...patch }))

  const save = async () => {
    if (!form.name.trim()) { setErrors({ name: 'Give the job a name, such as “Smith bathroom”.' }); return }
    setSaving(true)
    setError('')
    setErrors({})
    try {
      const saved = await saveJob({ ...form, job_id: isNew ? undefined : Number(jobId), customer_id: form.customer_id ? Number(form.customer_id) : null, measurement_id: form.measurement_id ? Number(form.measurement_id) : null })
      notify(isNew ? `${saved.job_number} added` : 'Job saved')
      if (isNew) nav(`/jobs/${saved.job_id}`, { replace: true })
      else await load()
    } catch (e) {
      setError(e.message || 'Couldn’t save the job.')
      setErrors(e.fields || {})
    } finally {
      setSaving(false)
    }
  }

  // A deposit invoice: held as customer deposits until the work is invoiced.
  const askDeposit = async () => {
    setDepositing(true)
    setError('')
    try {
      const res = await requestDeposit(job.job_id, Number(depositAmount))
      nav(`/sales/customer-invoices/${res.invoice_id}`)
    } catch (e) {
      setError(e.message || 'Couldn’t make the deposit invoice.')
      setDepositOpen(false)
    } finally {
      setDepositing(false)
    }
  }
  // A release invoice: bills retainage the customer held back.
  const askRelease = async () => {
    setReleasing(true)
    setError('')
    try {
      const res = await releaseRetainage(job.job_id, Number(releaseAmount))
      nav(`/sales/customer-invoices/${res.invoice_id}`)
    } catch (e) {
      setError(e.message || 'Couldn’t make the retainage invoice.')
      setReleaseOpen(false)
    } finally {
      setReleasing(false)
    }
  }
  const openDeposit = () => {
    setDepositAmount(job?.quoted > 0 ? String(Math.round(job.quoted * 0.3 * 100) / 100) : '')
    setDepositOpen(true)
  }

  if (loading) return <div className="ci-form-page"><Spinner label="Loading job…" /></div>

  const forJob = job ? `job=${job.job_id}${job.customer_id ? `&customer=${job.customer_id}` : ''}` : ''
  // A long job's headline profit is as the work is done.
  const asDone = !!(job?.track_progress && job?.progress && job.progress.profit != null)
  const open = job?.status === 'Open'
  const st = JOB_STATUS[job?.status] || JOB_STATUS.Open

  const details = (
    <div className="ci-form-meta-card mf-form">
      {!isNew && <div className="mf-card-title">Details</div>}
      <div className="ci-form-meta-top two">
        <Field label="Job name" error={errors.name} hint="What the customer would call it, e.g. “Smith bathroom” or “Unit 4 rewire”.">
          <input value={form.name} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field label="Customer (optional)" error={errors.customer_id} hint="The job’s quotes and invoices must be for this customer. It can’t change once it has any.">
          <select value={form.customer_id} onChange={(e) => set({ customer_id: e.target.value })}>
            <option value="">No customer yet</option>
            <CreateOption kind="customer" />
            {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{customerName(c)}</option>)}
          </select>
        </Field>
      </div>
      <div className="ci-form-meta-top two">
        <Field label="Site address (optional)">
          <input value={form.site_address} onChange={(e) => set({ site_address: e.target.value })} placeholder="Where the work is" />
        </Field>
        {!isNew && (
          <Field label="Status" error={errors.status} hint="Completed jobs leave the pickers; cancelled ones take nothing new.">
            <select value={form.status} onChange={(e) => set({ status: e.target.value })}>
              {Object.keys(JOB_STATUS).map((s) => <option key={s} value={s}>{JOB_STATUS[s].label}</option>)}
            </select>
          </Field>
        )}
      </div>
      <div className="ci-form-meta-top two">
        <Field label="Start date (optional)" error={errors.start_date}>
          <input type="date" value={form.start_date} onChange={(e) => set({ start_date: e.target.value })} />
        </Field>
        <Field label="End date (optional)" error={errors.end_date}>
          <input type="date" value={form.end_date} onChange={(e) => set({ end_date: e.target.value })} />
        </Field>
      </div>
      <div className="ci-form-meta-top">
        <label className="ci-form-toggle">
          <input type="checkbox" checked={form.track_progress} onChange={(e) => set({ track_progress: e.target.checked })} />
          <span>
            <strong>Long job: count income as the work is done</strong>{' '}
            <HelpTip text="For jobs that run across month-ends. Income then follows how far along the job is (cost so far ÷ expected cost), not the invoices. At month-end close, Accounting books the difference. Leave it off for short jobs." />
          </span>
        </label>
      </div>
      {form.track_progress && (
        <div className="ci-form-meta-top two">
          <Field label="Expected total cost" error={errors.estimated_cost} hint="Materials, subcontractors and other costs for the whole job as first planned. Used to work out how far along it is. Approved changes add their cost to it.">
            <input inputMode="decimal" value={form.estimated_cost} onChange={(e) => set({ estimated_cost: e.target.value })} placeholder="0.00" />
          </Field>
          <Field label="Price (optional)" error={errors.contract_price} hint="What the customer pays for the whole job as first agreed, before tax. Leave blank to use the job’s quotes. Approved changes are added to it.">
            <input inputMode="decimal" value={form.contract_price} onChange={(e) => set({ contract_price: e.target.value })} placeholder={job?.quoted ? `From the quotes` : '0.00'} />
          </Field>
        </div>
      )}
      <div className="ci-form-meta-top two">
        <Field label="Retainage held back (optional)" error={errors.retainage_percent} hint="The share of each bill the customer keeps until the work is accepted, e.g. 10%. Stage and change-order invoices hold it back by themselves.">
          <input inputMode="decimal" value={form.retainage_percent} onChange={(e) => set({ retainage_percent: e.target.value })} placeholder="0%" />
        </Field>
      </div>
      <div className="ci-form-meta-top">
        <Field label="Notes (optional)">
          <textarea className="ci-form-memo-textarea" value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
        </Field>
      </div>
    </div>
  )

  const actionBar = (
    <div className="ci-form-action-bar">
      <div className="ci-form-action-left">
        <button type="button" className="ci-btn ci-btn-primary" disabled={saving} onClick={save}><Save size={14} />{saving ? 'Saving…' : (isNew ? 'Add job' : 'Save details')}</button>
        {isNew && <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav('/jobs')}>Cancel</button>}
      </div>
    </div>
  )

  if (isNew) {
    return (
      <div className="ci-form-page">
        <DocHeader back={<BackLink to="/jobs" label="Jobs" />} title="New job" meta="One piece of work for a customer. Its quotes, invoices, bills and expenses add up here." />
        <JobsGuide />
        {error && <ErrorBanner message={error} />}
        {details}
        {actionBar}
      </div>
    )
  }

  if (!job) return <div className="ci-form-page">{error && <ErrorBanner message={error} />}</div>

  return (
    <div className="ci-form-page">
      <DocHeader
        back={<BackLink to="/jobs" label="Jobs" />}
        title={job.name}
        badge={<StatusBadge label={st.label} tone={st.tone} />}
        meta={[job.job_number, job.customer_name, job.site_address, job.start_date && `from ${fmtDay(job.start_date)}`].filter(Boolean).join(' · ')}
        actions={open && (
          <div className="ci-header-actions">
            <Link className="ci-btn ci-btn-ghost ci-btn-sm" to={`/sales/quotes/new?${forJob}`}><FilePlus2 size={14} />New quote</Link>
            <Link className="ci-btn ci-btn-ghost ci-btn-sm" to={`/sales/customer-invoices/new?${forJob}`}><FileText size={14} />New invoice</Link>
            {job.customer_id && <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={openDeposit}><PiggyBank size={14} />Request a deposit</button>}
            {job.retainage_to_bill > 0.004 && <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => { setReleaseAmount(String(job.retainage_to_bill)); setReleaseOpen(true) }}><LockOpen size={14} />Bill the retainage</button>}
            {has('purchase') && <Link className="ci-btn ci-btn-ghost ci-btn-sm" to={`/purchase/invoices/new?type=service&job=${job.job_id}`}><Truck size={14} />Record a bill</Link>}
            {has('expenses') && <Link className="ci-btn ci-btn-ghost ci-btn-sm" to={`/operating/new?job=${job.job_id}`}><Receipt size={14} />Record an expense</Link>}
          </div>
        )}
      />
      {error && <ErrorBanner message={error} />}
      <JobProfitGuide />

      <div className="sum-tiles">
        <Tile label="Quoted" href="#quotes" icon={ClipboardList} value={money(job.quoted)} note={job.quotes.length ? `${job.quotes.length} ${job.quotes.length === 1 ? 'quote' : 'quotes'}` : 'No quote yet'} />
        <Tile label="Invoiced" href="#invoices" icon={FileText} value={money(job.invoiced)}
          note={job.quoted > 0 ? `${Math.round((job.invoiced / job.quoted) * 100)}% of the quote` : 'Including tax'} />
        <Tile label="Owed" href="#invoices" icon={Wallet} value={money(job.owed)} note={job.owed > 0.004 ? 'Still to be paid' : 'Nothing owed'} tone={job.owed > 0.004 ? 'warn' : undefined} />
        {(Math.abs(job.changes_approved) > 0.004 || job.changes_waiting_count > 0) && (
          <Tile label="Changes" href="#changes" icon={GitPullRequestArrow} tone={job.changes_waiting_count ? 'warn' : undefined}
            value={signedMoney(Math.abs(job.changes_approved) > 0.004 ? job.changes_approved : job.changes_waiting)}
            note={Math.abs(job.changes_approved) > 0.004
              ? (job.changes_waiting_count ? `Approved, added to the price · ${job.changes_waiting_count} more waiting` : 'Approved, added to the price')
              : `${job.changes_waiting_count} waiting for the customer, not in the price yet`} />
        )}
        {job.retainage_held_back > 0.004 && (
          <Tile label="Retainage held" href="#invoices" icon={Lock} value={money(job.retainage_held)}
            note={job.retainage_to_bill > 0.004 ? `Held by the customer · ${money(job.retainage_to_bill)} still to bill` : `All ${money(job.retainage_held_back)} billed for release`} />
        )}
        {(job.deposits_received > 0.004 || job.deposit_held > 0.004) && (
          <Tile label="Deposit held" href="#invoices" icon={PiggyBank} value={money(job.deposit_held)}
            note={job.deposit_held > 0.004 ? 'Paid, not yet taken off an invoice' : `All ${money(job.deposits_received)} taken off`} />
        )}
        {asDone
          ? <Tile label="Profit so far" href="#progress" icon={TrendingUp} value={money(job.progress.profit)} note={`As the work is done · ${job.progress.percent}% done`} tone={job.progress.profit < 0 ? 'warn' : undefined} />
          : <Tile label="Profit" href="#profit" icon={TrendingUp} value={money(job.profit)} note={`Margin ${marginText(job.margin)}`} tone={job.profit < 0 ? 'warn' : undefined} />}
      </div>

      <div className="sum-columns">
        <div className="sum-main">
          <div id="profit">
            <Card title={asDone
              ? <>From the invoices <HelpTip text="This long job’s figures as invoiced: income counts when you invoice. Its profit as the work is done is under Progress, and month-end close books the difference." /></>
              : <>Profit on this job <HelpTip text="From the books: what the job’s invoices earned (less sales tax, discounts and credits) minus the cost of stock sold on them, its bills and its expenses." /></>}>
              <table className="ci-table job-profit">
                <tbody>
                  <tr><td>Earned <HelpTip text="The job’s posted invoices less sales tax, discounts and credits." /></td><td className="num">{money(job.revenue)}</td></tr>
                  <tr><td>Goods and materials <HelpTip text="Materials on the job’s bills, and the cost of your own stock sold on its invoices." /></td><td className="num">{money(-job.goods)}</td></tr>
                  <tr><td>Services and subcontractors <HelpTip text="Subcontractors and other services on the job’s bills." /></td><td className="num">{money(-job.services)}</td></tr>
                  <tr><td>Other expenses <HelpTip text="Expenses put on this job, such as fuel, parking or tool hire." /></td><td className="num">{money(-job.expenses)}</td></tr>
                  <tr className="ci-total"><td><strong>{asDone ? 'Profit as invoiced' : 'Profit'}</strong></td><td className="num"><strong>{money(job.profit)}</strong></td></tr>
                  <tr><td className="ci-muted-cell">Margin <HelpTip text="Profit as a share of earned." /></td><td className="num ci-muted-cell">{marginText(job.margin)}</td></tr>
                </tbody>
              </table>
            </Card>
          </div>

          {job.track_progress && job.progress && (
            <div id="progress">
            <Card title={<>Progress <HelpTip text="How far along the job is (cost so far ÷ expected cost), what that has earned, and how it compares with what’s been billed. At month-end close the difference is booked." /></>}>
              {job.progress.problem ? <p className="sum-warn">{job.progress.problem} to see its progress.</p> : (
                <table className="ci-table job-profit">
                  <tbody>
                    <tr><td>Done</td><td className="num"><strong>{job.progress.percent}%</strong></td></tr>
                    <tr><td>Cost so far / expected</td><td className="num">{money(job.progress.costs)} / {money(job.progress.estimated_cost)}</td></tr>
                    <tr><td>Earned ({job.progress.percent}% of {money(job.progress.price)}){Math.abs(job.progress.changes_price) > 0.004 && <> <HelpTip text={`The price includes approved changes of ${money(job.progress.changes_price)}, and the expected cost includes their ${money(job.progress.changes_cost)}.`} /></>}</td><td className="num">{money(job.progress.earned)}</td></tr>
                    <tr><td>Billed</td><td className="num">{money(job.progress.billed)}</td></tr>
                    {job.progress.under > 0.004 && <tr><td>Earned, not yet billed <HelpTip text="Work done that hasn’t been invoiced yet. Invoice it, or month-end close books it as a contract asset." /></td><td className="num">{money(job.progress.under)}</td></tr>}
                    {job.progress.over > 0.004 && <tr><td>Billed, not yet earned <HelpTip text="Invoiced ahead of the work. Month-end close books it as a contract liability until the work catches up." /></td><td className="num">{money(job.progress.over)}</td></tr>}
                    <tr className="ci-total"><td><strong>Profit so far, as the work is done</strong> <HelpTip text="Earned so far, less the job’s costs so far and any expected loss. This is the job’s profit the way the books show it after month-end close." /></td><td className="num"><strong>{money(job.progress.profit)}</strong></td></tr>
                    {job.progress.loss > 0.004 && <tr><td className="sum-warn">Expected loss still to book <HelpTip text="The job is expected to cost more than its price. Month-end close books the whole loss now." /></td><td className="num sum-warn">{money(job.progress.loss)}</td></tr>}
                  </tbody>
                </table>
              )}
            </Card>
            </div>
          )}

          {measurements && <JobMeasurements job={job} />}

          <JobPhotos job={job} />

          <JobChanges job={job} open={open} onChanged={load} onError={setError} />

          <div id="invoices">
            <Card title="Invoices" action={open && <Link to={`/sales/customer-invoices/new?${forJob}`}>New invoice</Link>}>
              {job.invoices.length ? (
                <ul className="sum-list">
                  {job.invoices.map((i) => (
                    <li key={i.invoice_id}>
                      <span className="sum-list-main"><Link to={`/sales/customer-invoices/${i.invoice_id}`}>{i.number || 'Draft invoice'}</Link><span className="cell-sub">{[fmtDay(i.date), i.status, i.kind === 'Deposit' ? 'Deposit' : i.kind === 'Retainage' ? 'Retainage release' : i.kind === 'Progress' ? `Stage: ${Number(i.progress_percent || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}%` : i.change_order ? `Change ${i.change_order}` : '', i.deposit_applied > 0.004 ? `${money(i.deposit_applied)} deposit taken off` : '', i.retainage_amount > 0.004 ? `${money(i.retainage_amount)} retainage held` : ''].filter(Boolean).join(' · ')}</span></span>
                      <span className="sum-list-side">{money(i.total)}{i.balance > 0.004 && <span className="cell-sub">{money(i.balance)} owed</span>}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="sum-muted">No invoices on this job yet.</p>}
            </Card>
          </div>
        </div>

        <div className="sum-side">
          <div id="quotes">
            <Card title="Quotes" action={open && <Link to={`/sales/quotes/new?${forJob}`}>New quote</Link>}>
              {job.quotes.length ? (
                <ul className="sum-list">
                  {job.quotes.map((q) => (
                    <li key={q.quote_id}>
                      <span className="sum-list-main"><Link to={`/sales/quotes/${q.quote_id}`}>{q.number || 'Draft quote'}</Link><span className="cell-sub">{[fmtDay(q.date), q.status, q.billed_pct > 0 ? `${Number(q.billed_pct).toLocaleString(undefined, { maximumFractionDigits: 1 })}% billed` : ''].filter(Boolean).join(' · ')}</span></span>
                      <span className="sum-list-side">{money(q.total)}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="sum-muted">No quotes on this job yet.</p>}
            </Card>
          </div>
          {(has('purchase') || job.bills.length > 0) && (
            <Card title={<>Bills <HelpTip text="Bills without a purchase order put on this job: materials bought for it and subcontractors. Record one from Record a bill." /></>} action={open && has('purchase') && <Link to={`/purchase/invoices/new?type=service&job=${job.job_id}`}>Record a bill</Link>}>
              {job.bills.length ? (
                <ul className="sum-list">
                  {job.bills.map((b) => (
                    <li key={b.invoice_id}>
                      <span className="sum-list-main"><Link to={`/purchase/invoices/${b.invoice_id}`}>{b.vendor_name || b.doc_no}</Link><span className="cell-sub">{[b.doc_no, b.number, fmtDay(b.date), BILL_STATUS[b.status] || b.status].filter(Boolean).join(' · ')}</span></span>
                      <span className="sum-list-side">{money(b.total)}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="sum-muted">No bills on this job yet. Record materials and subcontractors here.</p>}
            </Card>
          )}
          {(has('expenses') || job.expense_entries.length > 0) && (
            <Card title={<>Expenses <HelpTip text="Running costs put on this job, such as fuel, parking or tool hire." /></>} action={open && has('expenses') && <Link to={`/operating/new?job=${job.job_id}`}>Record an expense</Link>}>
              {job.expense_entries.length ? (
                <ul className="sum-list">
                  {job.expense_entries.map((x) => (
                    <li key={x.je_id}>
                      <span className="sum-list-main"><Link to={`/operating/expenses/${x.je_id}`}>{x.what || x.doc_no}</Link><span className="cell-sub">{fmtDay(x.date)}</span></span>
                      <span className="sum-list-side">{money(x.amount)}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="sum-muted"><Banknote size={13} aria-hidden="true" /> No expenses on this job yet, such as fuel or parking.</p>}
            </Card>
          )}
        </div>
      </div>

      {details}
      {actionBar}

      <Modal
        open={releaseOpen}
        onClose={() => setReleaseOpen(false)}
        title="Bill the retainage"
        subtitle={`An invoice to ${job.customer_name || 'the customer'} for retainage they held back, once the work is accepted. It carries no new income or tax: both were counted when the work was billed.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setReleaseOpen(false)}>Cancel</Button>
            <Button onClick={askRelease} loading={releasing} disabled={!(Number(releaseAmount) > 0)}><LockOpen size={14} />Make the invoice</Button>
          </>
        }
      >
        <label className="ci-form-field">
          <span>Amount to bill <HelpTip text="All of it at the end, or part at a milestone such as substantial completion." /></span>
          <input inputMode="decimal" value={releaseAmount} onChange={(e) => setReleaseAmount(e.target.value)} placeholder="0.00" />
          <span className="cell-sub">{money(job.retainage_to_bill)} held and not yet billed.</span>
        </label>
      </Modal>

      <Modal
        open={depositOpen}
        onClose={() => setDepositOpen(false)}
        title="Request a deposit"
        subtitle={`An invoice to ${job.customer_name || 'the customer'} for money up front. It’s held as a customer deposit (not income yet) until you invoice the work, and the final invoice takes it off.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDepositOpen(false)}>Cancel</Button>
            <Button onClick={askDeposit} loading={depositing} disabled={!(Number(depositAmount) > 0)}><PiggyBank size={14} />Make the deposit invoice</Button>
          </>
        }
      >
        <label className="ci-form-field">
          <span>Deposit amount <HelpTip text="No sales tax is charged on the deposit: the invoices for the work charge tax on all of it." /></span>
          <input inputMode="decimal" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} placeholder="0.00" />
          {job.quoted > 0 && <span className="cell-sub">30% of the quote is {money(job.quoted * 0.3)}.</span>}
        </label>
      </Modal>
    </div>
  )
}
